/**
 * Trigger Engine — Pure Functions
 * Evaluates triggers based on player actions and returns events.
 * No side effects, no mutations — just data in, data out.
 */

import type { GridMapState, Trigger, TriggerEvent, FogState } from './types';
import { coordKey } from './grid';

// ============================================================================
// HELPERS
// ============================================================================

/** Check if a trigger is active (enabled + not already fired if oneShot + flags met). */
function isActive(trigger: Trigger, firedTriggers: Set<string>, triggerFlags: Set<string>): boolean {
  if (trigger.enabled === false) return false;
  const isOneShot = trigger.oneShot !== false; // default true
  if (isOneShot && firedTriggers.has(trigger.id)) return false;
  if (trigger.requiredFlags) {
    for (const flag of trigger.requiredFlags) {
      if (!triggerFlags.has(flag)) return false;
    }
  }
  return true;
}

/** Manhattan distance between two points. */
function manhattan(x1: number, y1: number, x2: number, y2: number): number {
  return Math.abs(x1 - x2) + Math.abs(y1 - y2);
}

/** Build a Set of coord keys for all currently visible cells in the fog grid. */
export function extractVisibleSet(fogGrid: FogState[][]): Set<string> {
  const set = new Set<string>();
  for (let y = 0; y < fogGrid.length; y++) {
    const row = fogGrid[y];
    for (let x = 0; x < row.length; x++) {
      if (row[x] === 'visible') {
        set.add(coordKey(x, y));
      }
    }
  }
  return set;
}

// ============================================================================
// EVALUATORS
// ============================================================================

/** Fires when the player stands on a trigger cell. */
export function evaluateStepTriggers(
  triggers: Trigger[],
  firedTriggers: Set<string>,
  triggerFlags: Set<string>,
  playerX: number,
  playerY: number,
): TriggerEvent[] {
  const events: TriggerEvent[] = [];
  const playerKey = coordKey(playerX, playerY);

  for (const trigger of triggers) {
    if (trigger.condition !== 'step') continue;
    if (!isActive(trigger, firedTriggers, triggerFlags)) continue;

    for (const cell of trigger.cells) {
      if (coordKey(cell.x, cell.y) === playerKey) {
        events.push({
          triggerId: trigger.id,
          effect: trigger.effect,
          sourceCell: { x: cell.x, y: cell.y },
          playerPosition: { x: playerX, y: playerY },
        });
        break; // One event per trigger per evaluation
      }
    }
  }

  return events;
}

/** Fires when the player is within Manhattan radius of a trigger cell. */
export function evaluateProximityTriggers(
  triggers: Trigger[],
  firedTriggers: Set<string>,
  triggerFlags: Set<string>,
  playerX: number,
  playerY: number,
): TriggerEvent[] {
  const events: TriggerEvent[] = [];

  for (const trigger of triggers) {
    if (trigger.condition !== 'proximity') continue;
    if (!isActive(trigger, firedTriggers, triggerFlags)) continue;

    const radius = trigger.radius ?? 2;

    for (const cell of trigger.cells) {
      if (manhattan(playerX, playerY, cell.x, cell.y) <= radius) {
        events.push({
          triggerId: trigger.id,
          effect: trigger.effect,
          sourceCell: { x: cell.x, y: cell.y },
          playerPosition: { x: playerX, y: playerY },
        });
        break;
      }
    }
  }

  return events;
}

/** Fires when a trigger cell enters the player's field of view. */
export function evaluateVisibleTriggers(
  triggers: Trigger[],
  firedTriggers: Set<string>,
  triggerFlags: Set<string>,
  prevVisibleSet: Set<string>,
  currVisibleSet: Set<string>,
  playerX: number,
  playerY: number,
): TriggerEvent[] {
  const events: TriggerEvent[] = [];

  for (const trigger of triggers) {
    if (trigger.condition !== 'visible') continue;
    if (!isActive(trigger, firedTriggers, triggerFlags)) continue;

    for (const cell of trigger.cells) {
      const key = coordKey(cell.x, cell.y);
      // Newly visible: in current set but not in previous set
      if (currVisibleSet.has(key) && !prevVisibleSet.has(key)) {
        events.push({
          triggerId: trigger.id,
          effect: trigger.effect,
          sourceCell: { x: cell.x, y: cell.y },
          playerPosition: { x: playerX, y: playerY },
        });
        break;
      }
    }
  }

  return events;
}

/** Fires when the player explicitly interacts with an adjacent cell. */
export function evaluateInteractTriggers(
  triggers: Trigger[],
  firedTriggers: Set<string>,
  triggerFlags: Set<string>,
  targetX: number,
  targetY: number,
  playerX: number,
  playerY: number,
): TriggerEvent[] {
  const events: TriggerEvent[] = [];
  const targetKey = coordKey(targetX, targetY);

  for (const trigger of triggers) {
    if (trigger.condition !== 'interact') continue;
    if (!isActive(trigger, firedTriggers, triggerFlags)) continue;

    for (const cell of trigger.cells) {
      if (coordKey(cell.x, cell.y) === targetKey) {
        events.push({
          triggerId: trigger.id,
          effect: trigger.effect,
          sourceCell: { x: cell.x, y: cell.y },
          playerPosition: { x: playerX, y: playerY },
        });
        break;
      }
    }
  }

  return events;
}

// ============================================================================
// APPLY EVENTS
// ============================================================================

/**
 * Apply trigger events to the state:
 * - Mark one-shot triggers as fired
 * - Set trigger flags
 * - Spawn entities (for spawn effects)
 * - Append events to pendingEvents
 *
 * Returns the updated state. Does NOT handle teleport movement — that
 * is handled by the state machine after applyTriggerEvents returns.
 */
export function applyTriggerEvents(state: GridMapState, events: TriggerEvent[]): GridMapState {
  if (events.length === 0) return state;

  const triggers = state.map.triggers ?? [];
  const firedTriggers = new Set(state.firedTriggers);
  const triggerFlags = new Set(state.triggerFlags);
  let map = state.map;

  for (const event of events) {
    const trigger = triggers.find(t => t.id === event.triggerId);
    if (!trigger) continue;

    // Mark one-shot triggers as fired
    const isOneShot = trigger.oneShot !== false;
    if (isOneShot) {
      firedTriggers.add(trigger.id);
    }

    // Set flag
    if (trigger.setsFlag) {
      triggerFlags.add(trigger.setsFlag);
    }

    // Handle spawn effects — add entities to the map
    if (event.effect.type === 'spawn') {
      let entities = [...map.entities];
      for (const spec of event.effect.entities) {
        entities.push({
          id: `${event.triggerId}_${spec.name}_${spec.x}_${spec.y}`,
          x: spec.x,
          y: spec.y,
          type: spec.entityType,
          name: spec.name,
        });
      }
      map = { ...map, entities };
    }
  }

  return {
    ...state,
    map,
    firedTriggers,
    triggerFlags,
    pendingEvents: [...state.pendingEvents, ...events],
  };
}
